<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import type { JobOffer } from "@/model/backend/job-offer"
import { useHttpClient } from "@/vf"
import JobOfferItem from "@/shop/components/jobs/JobOfferItem.vue"

const loading = ref(true)
const error = ref<string | undefined>()
const offers = ref<JobOffer[]>([])
const http = useHttpClient()

// Synthetic initiative application offer
const initiativeOffer: JobOffer = {
    id: "__initiative__",
    title: "Initiativbewerbung",
    description: "Kein passender Job dabei? Dann freuen wir uns über deine Initiativbewerbung!",
}

async function loadMore() {
    const more = (await http.get<JobOffer[]>("/job-offer")).data
    offers.value.push(...more)
}

const visibleOffers = computed(() => {
    const list = [...offers.value]
    // Always append the initiative offer at the end
    list.push(initiativeOffer)
    return list
})

const expandedOfferId = computed(() => {
    const realCount = visibleOffers.value.length
    if (realCount === 1) return visibleOffers.value[0].id
    //if (realCount === 0) return initiativeOffer.id
    return undefined
})

async function load() {
    try {
        loading.value = true
        error.value = undefined
        await loadMore()
    } catch (e: any) {
        error.value = e?.message ?? "Fehler beim Laden der Jobangebote"
    } finally {
        loading.value = false
    }
}

onMounted(load)
</script>

<template>
    <main aria-labelledby="jobs">
        <h1 id="jobs" class="sr-only">Jobs</h1>

        <div class="container" role="presentation">
            <div class="breadcrumb" aria-hidden="true">
                <RouterLink class="breadcrumb-item" :to="{ name: 'home' }">Home</RouterLink>
                <div class="breadcrumb-item">Jobs</div>
            </div>

            <section class="page-intro pb-3">
                <h2 class="h3 mb-1">Karriere bei CANN24</h2>
                <p class="mb-0">
                    Du möchtest ein Teil von CANN24 werden und gerne mit uns arbeiten?
                    <span v-if="offers.length >= 1">Hier findest du unsere aktuellen Ausschreibungen.</span
                    ><span v-else
                        >Momentan haben wir keine Stellen ausgeschrieben. Gerne kannst du dich initiativ bei uns
                        bewerben.</span
                    >
                    Schick uns gleich deine Bewerbung an <a href="mailto:jobs@cann24.de">jobs@cann24.de</a>. Wir freuen
                    uns auf dich.
                </p>
            </section>

            <div v-if="loading" class="py-5 text-center">Lade Jobangebote…</div>
            <div v-else-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
            <div v-else>
                <div v-if="visibleOffers.length < 1" class="py-5 text-center">
                    <h3 class="h5 mb-2">Aktuell keine ausgeschriebenen Stellen</h3>
                </div>

                <div v-if="visibleOffers.length >= 1" class="job-list">
                    <JobOfferItem
                        v-for="offer in visibleOffers"
                        :key="offer.id"
                        :offer="offer"
                        :expanded="offer.id === expandedOfferId"
                    />
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
.job-list {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr; /* immer volle Zeile */
}

.page-intro {
    border-bottom: 1px solid #eef0f2;
    margin-bottom: 1rem;
}
</style>
